import React, { lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import classNames from 'classnames';
import { Toaster } from 'sonner';

import { CatalogueTemplate } from '../../shop/catalogue/CatalogueTemplate';
import { CheckoutThanks } from '../../shop/checkout/CheckoutThanks';
import { OrdersView } from '../../shop/orders/OrdersView';
import OrdersDetailsView from '../../shop/orders/OrdersDetailsView';
import { BundleDetailPage } from '../bundles/BundleDetailPage';
import { PageNotFound } from '../../shop/PageNotFound';
import NotificationContainer from '../notification/NotificationContainer';
import { HeaderContainer } from '../header/HeaderContainer';
import LibxRouteSwitch from '../../yourborrowbox/libx/LibxRouteSwitch';
import withErrorLog from '../../shared/utils/withErrorLog';
import TermsAndConditions from '../../shop/termsandconditions/TermsAndConditions';
import CollectionRouteSwitch from '../../yourborrowbox/collection/RouteSwitch';
import LocalContentRouteSwitch from '../../yourborrowbox/localcontent/LocalContentRouteSwitch';
import LanguageRouteSwitch from '../../shop/language/RouteSwitch';
import CampaignRouteSwitch from '../../shop/unlimited/RouteSwitch';
import MarketingRouteSwitch from '../../yourborrowbox/marketing/MarketingRouteSwitch';
import Footer from './Footer';
import DocumentTitle from './DocumentTitle';
import { MODAL_ROOT_CLASS } from './modal/ModalLayout';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CheckRequiredSpoc, SpocRequiredFor } from '../common/CheckRequiredSpoc/CheckRequiredSpoc';
import { EnvironmentProvider } from './EnvironmentContext';
import { MaintenanceWrapper } from '../../shop/MaintenanceWrapper';
import { QueryClientProvider } from '@tanstack/react-query';
import { browserClient } from '../../react-query';
import { CartCheckout } from '../../shop/cart/checkout/CartCheckout';
import { ScrollRestoration } from '../utils/scroll/ScrollRestoration';
import { SelectionListPage } from '../../shop/selectionlists/SelectionListPage';
import { SearchResultPage } from '../search/SearchResultPage';
import { CartPage } from '../../shop/cart/CartPage';
import { ReportingPage } from '../../yourborrowbox/reporting/ReportingPage';
import { DropdownProvider } from '../../hooks/dropdownContext';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { ShopTemplate } from '../../shop/catalogue/ShopTemplate';
import ProductDetailPage from '../product/detail/ProductDetailPage';
import { CheckLoanFormat } from './CheckLoanFormat';
import { FeaturedEntryPage } from '../../shop/entrypages/featured/FeaturedEntryPage';
import { ProductSelectionProvider } from '../product-selection/ProductSelectionContext';
import CollectionManagementRouteSwitch from '../../yourborrowbox/collection/CollectionManagementRouteSwitch';
import MerchandisingRouteSwitch from '../../yourborrowbox/marketing/MerchandisingRouteSwitch';
import { useTranslation } from 'react-i18next';
import { DefaultLanguageRedirect } from './DefaultLanguageRedirect';
import { SelectionListBarProvider } from '../selection-list/SelectionListBarContext';
import { LoadingWrapper } from './LoadingWrapper';
import LoadingOverlay from './LoadingOverlay';
import { GlobalContextProvider } from '../../app-store/GlobalContextProvider';
import { AssistantPage } from '../../shop/assistant/AssistantPage';

const UserManagementPageDeferred = lazy(() =>
    import('../../yourborrowbox/usermanagement/UserManagementPage').then(module => ({
        default: module.UserManagementPage
    }))
);

//TODO: move to core
const App = ({ store, maintenance = false }: { store: any; maintenance?: boolean }) => {
    const loadingClasses = classNames({ sticky: false });
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { t } = useTranslation();

    return (
        <QueryClientProvider client={browserClient}>
            <Provider store={store}>
                <GlobalContextProvider>
                    <BrowserRouter>
                        <DefaultLanguageRedirect>
                            <MaintenanceWrapper active={maintenance}>
                                <EnvironmentProvider>
                                    <DropdownProvider>
                                        <ScrollRestoration>
                                            <DocumentTitle title={t('components.documentTitle.shop')} />
                                            <div id={MODAL_ROOT_CLASS} />
                                            <div className={loadingClasses} id="loading-root" />
                                            <NotificationContainer />
                                            <Toaster />
                                            <SelectionListBarProvider>
                                                <HeaderContainer />
                                                <div className="content-area">
                                                    <Routes>
                                                        <Route
                                                            index
                                                            element={
                                                                <Navigate
                                                                    to={
                                                                        isMultiLanguageShopEnabled
                                                                            ? 'shop'
                                                                            : 'catalogue'
                                                                    }
                                                                    replace
                                                                />
                                                            }
                                                        />
                                                        <Route
                                                            path="index.html"
                                                            element={
                                                                <Navigate
                                                                    to={
                                                                        isMultiLanguageShopEnabled
                                                                            ? '/shop'
                                                                            : '/catalogue'
                                                                    }
                                                                    replace
                                                                />
                                                            }
                                                        />
                                                        <Route path="catalogue">
                                                            <Route index element={<Navigate to="featured" replace />} />
                                                            <Route
                                                                path="featured"
                                                                element={
                                                                    !isMultiLanguageShopEnabled ? (
                                                                        <FeaturedEntryPage />
                                                                    ) : (
                                                                        <Navigate to="/shop/featured" replace />
                                                                    )
                                                                }
                                                            />
                                                            <Route
                                                                path=":loanFormat/*"
                                                                element={
                                                                    <CheckLoanFormat>
                                                                        <CatalogueTemplate />
                                                                    </CheckLoanFormat>
                                                                }
                                                            />
                                                        </Route>
                                                        <Route path="shop">
                                                            <Route index element={<Navigate to="featured" replace />} />
                                                            <Route
                                                                path="featured"
                                                                element={
                                                                    isMultiLanguageShopEnabled ? (
                                                                        <FeaturedEntryPage />
                                                                    ) : (
                                                                        <Navigate to="/catalogue" replace />
                                                                    )
                                                                }
                                                            />
                                                            <Route path=":shopLanguage/*" element={<ShopTemplate />} />
                                                        </Route>
                                                        <Route path="products">
                                                            <Route path=":productId" element={<ProductDetailPage />} />
                                                        </Route>
                                                        <Route path="orderlists">
                                                            <Route
                                                                path=":id"
                                                                element={
                                                                    <CheckRequiredSpoc type={SpocRequiredFor.ORDERLIST}>
                                                                        <ProductSelectionProvider>
                                                                            <SelectionListPage />
                                                                        </ProductSelectionProvider>
                                                                    </CheckRequiredSpoc>
                                                                }
                                                            />
                                                        </Route>
                                                        <Route path="bundles">
                                                            <Route
                                                                path=":bundleId"
                                                                element={
                                                                    <ProductSelectionProvider>
                                                                        <BundleDetailPage />
                                                                    </ProductSelectionProvider>
                                                                }
                                                            />
                                                        </Route>
                                                        <Route
                                                            path="cart"
                                                            element={
                                                                <ProductSelectionProvider>
                                                                    <CartPage />
                                                                </ProductSelectionProvider>
                                                            }
                                                        />
                                                        <Route path="checkout">
                                                            <Route index element={<CartCheckout />} />
                                                            <Route path="thanks" element={<CheckoutThanks />} />
                                                        </Route>
                                                        <Route path="orders">
                                                            <Route index element={<OrdersView />} />
                                                            <Route
                                                                path=":id"
                                                                element={
                                                                    <CheckRequiredSpoc type={SpocRequiredFor.ORDER}>
                                                                        <OrdersDetailsView />
                                                                    </CheckRequiredSpoc>
                                                                }
                                                            />
                                                        </Route>
                                                        <Route
                                                            path="search/:pathParam1?/:pathParam2?/*"
                                                            element={
                                                                <ProductSelectionProvider>
                                                                    <SearchResultPage />
                                                                </ProductSelectionProvider>
                                                            }
                                                        />
                                                        <Route path="assistant/*" element={<AssistantPage />} />
                                                        <Route path="reporting/*" element={<ReportingPage />} />
                                                        <Route
                                                            path="collection/*"
                                                            element={<CollectionRouteSwitch />}
                                                        />
                                                        <Route
                                                            path="localcontent/*"
                                                            element={<LocalContentRouteSwitch />}
                                                        />
                                                        <Route
                                                            path="terms-conditions"
                                                            element={<TermsAndConditions />}
                                                        />
                                                        <Route path="libxchange/*" element={<LibxRouteSwitch />} />
                                                        <Route path="language/*" element={<LanguageRouteSwitch />} />
                                                        <Route path="unlimited/*" element={<CampaignRouteSwitch />} />
                                                        <Route path="marketing/*" element={<MarketingRouteSwitch />} />
                                                        <Route
                                                            path="collectionmgmt/*"
                                                            element={<CollectionManagementRouteSwitch />}
                                                        />
                                                        <Route
                                                            path="usermanagement/*"
                                                            element={
                                                                <Suspense
                                                                    fallback={
                                                                        <LoadingWrapper>
                                                                            <LoadingOverlay />
                                                                        </LoadingWrapper>
                                                                    }
                                                                >
                                                                    <UserManagementPageDeferred basePathname="/usermanagement" />
                                                                </Suspense>
                                                            }
                                                        />
                                                        <Route
                                                            path="merchandising/*"
                                                            element={<MerchandisingRouteSwitch />}
                                                        />
                                                        <Route element={<PageNotFound />} />
                                                    </Routes>
                                                </div>
                                            </SelectionListBarProvider>
                                            <div style={{ clear: 'both', display: 'block' }} />
                                            <Footer />
                                        </ScrollRestoration>
                                    </DropdownProvider>
                                </EnvironmentProvider>
                            </MaintenanceWrapper>
                        </DefaultLanguageRedirect>
                    </BrowserRouter>
                    {process.env.REACT_APP_DISABLE_QUERY_DEVTOOLS ? null : <ReactQueryDevtools initialIsOpen={false} />}
                </GlobalContextProvider>
            </Provider>
        </QueryClientProvider>
    );
};

export default withErrorLog(App);
